import React, { useEffect, useState } from 'react';

/**
 * Downloads for trips, on phones and tablets: a 720p copy (about 2 Mbps) the server makes on the fly is kept in this
 * browser's storage, with English subtitles and the poster. They play from /offline/, a page of its own whose service
 * worker serves them with no internet. The main app itself is never served by a service worker.
 * The browser has to stay on Zflix until a download finishes (a 45-minute episode takes about 10 minutes).
 */
export const OFFLINE_CACHE = 'zflix-offline-v1';
const META_KEY = 'zflix_offline_items';

export function offlineSupported() {
  if (typeof window === 'undefined' || !('caches' in window) || !('serviceWorker' in navigator) || typeof TransformStream === 'undefined') return false;
  return !document.documentElement.classList.contains('zflix-tv-apk-density');
}

/** The offline page's service worker, registered for /offline/ only, so the page is ready before the first trip. */
export function registerOfflinePage() {
  if (!offlineSupported()) return;
  navigator.serviceWorker.register('/offline/sw.js', { scope: '/offline/' }).catch(() => {});
}

const readMeta = () => {
  try {
    const d = JSON.parse(window.localStorage.getItem(META_KEY) || '[]');
    return Array.isArray(d) ? d : [];
  } catch (_) {
    return [];
  }
};
const writeMeta = (list) => {
  try {
    window.localStorage.setItem(META_KEY, JSON.stringify(list));
  } catch (_) {}
};

/** id -> { state: 'downloading' | 'done' | 'failed', got, expected, error } for this page's lifetime, plus saved ones. */
const store = { jobs: {}, subs: new Set() };
const emit = () => store.subs.forEach((f) => f({ ...store.jobs }));
for (const m of typeof window !== 'undefined' ? readMeta() : []) store.jobs[m.id] = { state: 'done', got: m.bytes, expected: m.bytes };

export function useDownloads() {
  const [jobs, setJobs] = useState({ ...store.jobs });
  useEffect(() => {
    store.subs.add(setJobs);
    return () => {
      store.subs.delete(setJobs);
    };
  }, []);
  return jobs;
}

export async function startDownload(id) {
  if (store.jobs[id]?.state === 'downloading') return;
  const set = (v) => {
    store.jobs[id] = { ...(store.jobs[id] || {}), ...v };
    emit();
  };
  const ac = new AbortController();
  set({ state: 'downloading', got: 0, expected: 0, error: '', abort: () => ac.abort() });
  try {
    const info = await fetch(`/api/offline/${encodeURIComponent(id)}/info`, { credentials: 'include' }).then((r) => {
      if (!r.ok) throw new Error('This one cannot be downloaded');
      return r.json();
    });
    if (navigator.storage?.persist) navigator.storage.persist().catch(() => {});
    const est = await navigator.storage?.estimate?.().catch(() => null);
    if (est && est.quota && est.usage != null && est.quota - est.usage < info.expectedBytes * 1.1) throw new Error('Not enough room on this device');
    const res = await fetch(`/api/offline/${encodeURIComponent(id)}/video`, { credentials: 'include', signal: ac.signal });
    if (!res.ok || !res.body) throw new Error('The server could not make a copy');
    const expected = info.expectedBytes || Number(res.headers.get('X-Zflix-Expected-Bytes')) || 0;
    let got = 0;
    let last = 0;
    const counter = new TransformStream({
      transform(chunk, ctl) {
        got += chunk.byteLength;
        if (got - last > 2_000_000) {
          last = got;
          set({ got, expected });
        }
        ctl.enqueue(chunk);
      },
    });
    const cache = await caches.open(OFFLINE_CACHE);
    await cache.put(`/offline/media/${id}.mp4`, new Response(res.body.pipeThrough(counter), { headers: { 'Content-Type': 'video/mp4' } }));
    if (got < 1_000_000) throw new Error('The copy came out empty');
    let subs = false;
    if (info.hasSubs) {
      const s = await fetch(`/api/offline/${encodeURIComponent(id)}/subs.vtt`, { credentials: 'include' }).catch(() => null);
      if (s && s.ok) {
        await cache.put(`/offline/media/${id}.vtt`, new Response(await s.text(), { headers: { 'Content-Type': 'text/vtt' } }));
        subs = true;
      }
    }
    if (info.poster) {
      const p = await fetch(info.poster, { credentials: 'include' }).catch(() => null);
      if (p && p.ok) await cache.put(`/offline/media/${id}.jpg`, new Response(await p.blob(), { headers: { 'Content-Type': p.headers.get('Content-Type') || 'image/jpeg' } }));
    }
    writeMeta([...readMeta().filter((m) => m.id !== id), { id, title: info.title, sub: info.sub || '', bytes: got, subs, at: Date.now() }]);
    set({ state: 'done', got, expected: got, abort: null });
  } catch (e) {
    caches.open(OFFLINE_CACHE).then((c) => c.delete(`/offline/media/${id}.mp4`)).catch(() => {});
    set({ state: 'failed', error: e.name === 'AbortError' ? 'Cancelled' : e.message || 'Download failed', abort: null });
  }
}

export async function removeDownload(id) {
  const cache = await caches.open(OFFLINE_CACHE);
  await Promise.all(['mp4', 'vtt', 'jpg'].map((x) => cache.delete(`/offline/media/${id}.${x}`)));
  writeMeta(readMeta().filter((m) => m.id !== id));
  delete store.jobs[id];
  emit();
}

const mb = (n) => `${Math.round((n || 0) / 1_000_000)} MB`;

/** Download / progress / downloaded, for a library movie or episode. Nothing on a TV or a browser that cannot keep files. */
export function OfflineButton({ id, compact = false }) {
  const jobs = useDownloads();
  if (!offlineSupported() || !id) return null;
  const j = jobs[id];
  const base = { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 6, padding: compact ? '6px 12px' : '8px 14px', fontSize: compact ? 12 : 13, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: 'var(--text)' };
  if (j?.state === 'downloading') {
    const pct = j.expected ? Math.min(99, Math.round((j.got / j.expected) * 100)) : 0;
    return (
      <button type="button" style={base} onClick={() => j.abort?.()} title="Cancel the download">
        {pct ? `Downloading ${pct}%` : `Downloading ${mb(j.got)}`} · Cancel
      </button>
    );
  }
  if (j?.state === 'done') {
    return (
      <button type="button" style={{ ...base, color: 'var(--text2)' }} onClick={() => { window.location.href = '/offline/'; }} title="Open Downloads">
        ✓ Downloaded
      </button>
    );
  }
  return (
    <button type="button" style={base} onClick={() => startDownload(id)} title={j?.state === 'failed' ? j.error : 'Keep a copy on this device for watching with no internet'}>
      {j?.state === 'failed' ? `Download again (${j.error})` : '⬇ Download'}
    </button>
  );
}
